import { EntityCard } from "./EntityCard";
import { EntityTypeBadge } from "./entityTypeMeta";
import { Badge } from "../../base/badge";
import { ChevronRight } from "lucide-react";
import { cn } from "@charphq/ui/lib/utils";
import { simCardStatusOption } from "@charphq/ui/utils/simCardStatus";

/** A BICS endpoint, as universal search finds it (OTO-155). */
interface SimCard {
  id: string;
  iccid: string;
  /** The BICS endpoint name, e.g. `Maxi_A3F1B2`. */
  name?: string;
  /** The BICS lifecycle code (`A`, `S`, …). */
  status?: string;
  device?: {
    id: string;
    name: string;
  };
}

interface SimCardCardProps {
  simCard: SimCard;
  onClick?: () => void;
  className?: string;
  /** Highlight the card as the active keyboard selection. */
  isSelected?: boolean;
}

export function SimCardCard({ simCard, onClick, className, isSelected }: SimCardCardProps) {
  const status = simCard.status ? simCardStatusOption(simCard.status) : null;

  return (
    <EntityCard
      onClick={onClick}
      className={cn(className, isSelected && "border-primary ring-2 ring-primary/50 shadow-md")}
    >
      <div className="flex h-full items-center justify-between">
        {/* Left side - Main content */}
        <div className="flex-1 min-w-0">
          {/* Top row: SIM Card type badge and status */}
          <div className="flex items-center justify-between mb-2">
            <EntityTypeBadge kind="simcard" />
            {status && (
              <Badge variant={status.variant} className="text-xs shrink-0 ml-2">
                {status.label}
              </Badge>
            )}
          </div>

          {/* Second row: BICS name and ICCID */}
          <div className="flex items-center gap-4 text-sm text-muted-foreground mb-2 min-w-0">
            {simCard.name && (
              <span className="font-medium text-foreground truncate">{simCard.name}</span>
            )}
            <span className="font-mono text-xs">{simCard.iccid}</span>
          </div>

          {/* Third row: Linked device */}
          {simCard.device && (
            <div className="text-xs text-muted-foreground">Linked to: {simCard.device.name}</div>
          )}
        </div>

        {/* Right side - Chevron */}
        <div className="flex flex-col items-end gap-2 shrink-0 ml-4">
          <ChevronRight className="h-4 w-4 text-muted-foreground" />
        </div>
      </div>
    </EntityCard>
  );
}
